import { API } from 'aws-amplify';
import {
  API_DATA_REQUESTED,
  API_ERROR,
  SET_OBSTACLE_ID,
  SET_OBSTACLE_NAME,
  SET_OBSTACLE_GEOJSON,
  SET_OBSTACLE_GEOHASH,
  SET_OBSTACLE_GROUNDRISK,
  SET_OBSTACLE_CATEGORY,
  SET_OBSTACLE_CELL_ID,
  OBSTACLES_RECEIVED,
  OBSTACLE_SAVED,
  CLEAR_OBSTACLE_STATE,
  OBSTACLE_REMOVED,
  QUEUE_NOTIFICATION
} from './actionTypes';

import { requestOptions, pecanSquareCellId } from '../constants';
import { store } from '../store/index';

type GeoJsonType = {
  type: string;
  features: any[];
};

type ObstacleType = {
  id?: string;
  name: string;
  geohash: string;
  cellId: number;
  geoJson: GeoJsonType;
  groundRisk: string;
  note?: string;
  category?: string;
};

const options = requestOptions;

export const clearState = (): ((dispatch: any) => void) => dispatch =>
  dispatch({
    type: CLEAR_OBSTACLE_STATE
  });

// Other actions follow similar pattern

export const getObstaclesV1Api = async (cellId: number) => {
  return await API.get('flightapi', `/obstacle/list/${cellId}`, options);
}

export const getObstaclesV2Api = async (cellId: number) => {

  if (cellId === pecanSquareCellId) { throw new Error('invalid_cell_id: pecan square cell (or any US cell) is forbidden from using this endpoint due to regulatory requirements') }

  const allCells = store.getState().orders.cells;
  const currentCell = allCells?.find(cell => cell?.id === cellId);

  if (!currentCell) { throw new Error(`cell with cellId ${cellId} not found`) }

  return await API.get('flightapi', `/timebasedobstacle/list-active/${cellId}?timezone=${currentCell.timezone}`, options)
}

/***
  This uses a merged result of `getObstaclesV1` and `getObstaclesV2` for all locations except PecanSquare in the USA.

  For the PecanSquare cell only the result of getObstaclesV1 is used, getObstaclesV2 must not be used in the US (Pecan Sqaure - CellId: 8) due to regulatory requirements
 */
export const getObstacles = (
  cellId: number,
  useNewEndpoint: boolean = true
): ((dispatch: any) => Promise<void>) => async (dispatch) => {

  let shouldReturnCombinedDataFromBothEndpoints = useNewEndpoint && cellId !== pecanSquareCellId;

  dispatch({ type: API_DATA_REQUESTED });

  try {
    const obstaclesV1Promise = getObstaclesV1Api(cellId);
    const obstaclesV2Promise = shouldReturnCombinedDataFromBothEndpoints ? getObstaclesV2Api(cellId) : Promise.resolve([]);

    const [res1, res2] = await Promise.all([obstaclesV1Promise, obstaclesV2Promise]);
    const combinedResult = res2.length ? [...res1, ...res2] : res1;

    dispatch({
      type: OBSTACLES_RECEIVED,
      payload: combinedResult,
    });

  } catch (err) {

    dispatch({
      type: API_ERROR,
      payload: err,
    });
  }
};

export const newObstacle = (
  groundRisk: number,
  name: string,
  cell: number,
  geoJson: GeoJsonType
): ((dispatch: any) => void) => dispatch => {
  dispatch({ type: API_DATA_REQUESTED });

  const body = {
    name: name,
    geohash: 'obstacle.geohash,',
    cellId: cell,
    geoJson,
    groundRisk: groundRisk,
    note: null
  };

  return API.post('flightapi', '/obstacle/add', { body })
    .then(() => {
      dispatch({
        type: OBSTACLE_SAVED
      });

      dispatch({
        type: QUEUE_NOTIFICATION,
        payload: {
          variant: 'success',
          message: 'Obstacle created successfully '
        }
      });
      dispatch(getObstacles(2));
    })
    .catch(err => {
      dispatch({
        type: API_ERROR,
        payload: err
      });
    });
};

export const addObstacle = (
  obstacle: ObstacleType,
  geoJson: GeoJsonType,
  cell: number
): ((dispatch: any) => void) => dispatch => {
  dispatch({ type: API_DATA_REQUESTED });

  const body = {
    name: obstacle.name,
    geohash: obstacle.geohash,
    cellId: cell,
    geoJson: {
      type: 'FeatureCollection',
      features: [geoJson]
    },
    groundRisk: obstacle.groundRisk,
    note: obstacle.note
  };

  API.post('flightapi', '/obstacle/add', { body })
    .then(() => {
      dispatch({
        type: OBSTACLE_SAVED
      });

      dispatch({
        type: QUEUE_NOTIFICATION,
        payload: {
          variant: 'success',
          message: 'Obstacle created successfully '
        }
      });
      dispatch(getObstacles(2));
    })
    .catch(err => {
      dispatch({
        type: API_ERROR,
        payload: err
      });
    });
};

export const updateObstacle = (
  obstacle: ObstacleType,
  groundRisk: number,
  name: string,
  cell: number,
  geoJson: GeoJsonType
): ((dispatch: any) => void) => dispatch => {
  dispatch({ type: API_DATA_REQUESTED });

  const body = {
    id: obstacle.id,
    cellId: obstacle.cellId,
    name: name || obstacle.name,
    geohash: obstacle.geohash,
    geoJson,
    groundRisk: groundRisk || obstacle.groundRisk,
    note: obstacle.note,
    category: obstacle.category
  };

  API.post('flightapi', '/obstacle/update', { body })
    .then(() => {
      dispatch({
        type: OBSTACLE_SAVED
      });

      dispatch({
        type: QUEUE_NOTIFICATION,
        payload: {
          variant: 'success',
          message: 'Obstacle updated successfully '
        }
      });
      dispatch(getObstacles(cell));
    })
    .catch(err => {
      dispatch({
        type: API_ERROR,
        payload: err
      });
    });
};

export const deleteObstacle = (
  id: string,
  cell: number
): ((dispatch: any) => void) => dispatch => {
  dispatch({ type: API_DATA_REQUESTED });

  return API.post('flightapi', `/obstacle/delete?obstacleId=${id}`, options)
    .then(() => {
      dispatch({
        type: OBSTACLE_REMOVED
      });

      dispatch({
        type: QUEUE_NOTIFICATION,
        payload: {
          variant: 'success',
          message: 'Obstacle deleted successfully '
        }
      });
      dispatch(getObstacles(cell));
      dispatch(clearState());
    })
    .catch(err => {
      dispatch({
        type: API_ERROR,
        payload: err
      });
    });
};

export const setId = (id: string): ((dispatch: any) => void) => dispatch =>
  dispatch({
    type: SET_OBSTACLE_ID,
    payload: id
  });

export const setName = (name: string): ((dispatch: any) => void) => dispatch =>
  dispatch({
    type: SET_OBSTACLE_NAME,
    payload: name
  });

export const setCategory = (
  name: string
): ((dispatch: any) => void) => dispatch =>
  dispatch({
    type: SET_OBSTACLE_CATEGORY,
    payload: name
  });

export const setGeoJson = (
  geoJson: GeoJsonType
): ((dispatch: any) => void) => dispatch =>
  dispatch({
    type: SET_OBSTACLE_GEOJSON,
    payload: geoJson
  });

export const setGeohash = (
  geohash: string
): ((dispatch: any) => void) => dispatch =>
  dispatch({
    type: SET_OBSTACLE_GEOHASH,
    payload: geohash
  });

export const setGroundRisk = (
  groundRisk: string
): ((dispatch: any) => void) => dispatch =>
  dispatch({
    type: SET_OBSTACLE_GROUNDRISK,
    payload: groundRisk
  });

export const setCellId = (
  cellId: number
): ((dispatch: any) => void) => {
  return (dispatch: any) => {
    return dispatch({
      type: SET_OBSTACLE_CELL_ID,
      payload: cellId
    });
  };
};

export const getOsmObstacleDataForCoordinates = async (lat: number, lng: number) => {
  return API.get('api', `/drop-location/osm/risk/${lat}/${lng}`, options);
};